import { Component } from 'react';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import DropList from 'components/atoms/DropList';
import Form from 'components/atoms/Form';
import FormGroup from 'components/atoms/FormGroup';
import Icon from 'components/atoms/Icon';
import Input from 'components/atoms/Input';
import Label from 'components/atoms/Label';
import Message from 'components/atoms/Message';
import { bindActionCreators, Dispatch } from 'redux';
import { SetGridSettingsAction, setGridSettingsAction } from 'store/actions/layoutActions';
import { EnviroDisplayOrderType, PortAreaType, PortType, RootState } from 'types';
import { GridColumnInterface, GridSettingsInterface } from 'types/functions';
import { ActionsType } from 'types/system';
import { clone, getId, renderError, setKey } from 'utils/formHandlers';
import { translate } from 'utils/translate';

interface AddGridColumnModelProps {
    //unused props
    children?: string | JSX.Element | JSX.Element[];

    //unused props
    language: string;

    id?: string;

    //unused props
    data: any;
    gridSettings: GridSettingsInterface;
    actions: ActionsType | undefined;

    //unused props
    setGridSettingsAction: SetGridSettingsAction;

    port: PortType;
}

interface AddGridColumnModelState {
    portAreas: PortAreaType[];
    column: GridColumnInterface;
    errors: any;
    columns: GridColumnInterface[];
    options: { value: string; label: string }[];
    enviroDisplayOrder: EnviroDisplayOrderType[];
}

class AddGridColumnModel extends Component<AddGridColumnModelProps, AddGridColumnModelState> {
    constructor(props: AddGridColumnModelProps) {
        super(props);
        this.state = {
            errors: {},
            enviroDisplayOrder: props.port.meta_data.enviroDisplayOrder,
            column: {
                order: true,
                uuid: getId(),
                meta_data: {},
                key: 'regionArea',
                orderDirection: 'desc',
                orderType: 'number',
                size: 2,
                status: true,
                label: '',
            },
            portAreas:
                props.port.port_areas?.filter((x) => {
                    return x.type === 'regionAreas';
                }) || [],
            columns: props.data.columns || props.gridSettings.columns,
            options: [
                { value: 'heading_deg', label: translate('heading') },
                { value: 'rot_deg_min', label: translate('rot') },
                { value: 'crot_deg_min', label: translate('crot') },
                { value: 'drift_angle_deg', label: translate('drift angle') },
                { value: 'cog_deg', label: translate('cog') },
                { value: 'sog_mps', label: translate('sog') },
                { value: 'wind', label: translate('wind') },
                { value: 'tide', label: translate('tide') },
                { value: 'wave', label: translate('wave') },
                { value: 'watercurrent', label: translate('water current') },
            ],
        };
    }

    hasErrorsDeep = (obj: any): boolean => {
        if (!obj) return false;
    
        if (typeof obj === 'string') {
            return obj.trim() !== '';
        }
    
        if (typeof obj !== 'object') {
            return obj !== null && obj !== undefined;
        }
    
        return Object.values(obj).some((value) => this.hasErrorsDeep(value));
    };


    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();

        const hasErrors = this.hasErrorsDeep(this.state.errors);

        if (hasErrors) {
            return; // stop submit
        }

        this.props.actions?.onConfirm(this.state.column);
        this.props.actions?.onCancel();
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    handleChange = (name, value) => {
        const clear = {
            'meta_data.option': ['meta_data.port_url_id', 'meta_data.parameter'],
            'meta_data.port_url_id': ['meta_data.parameter'],
        };
        const toClear = clear[name];
        const errors = clone(this.state.errors);
        const column = clone(this.state.column);
        setKey(column, name, value);
        setKey(errors, name, null);

        if (toClear) {
            toClear.forEach((x) => {
                setKey(column, x, null);
            });
        }

        this.setState(
            {
                column,
                errors,
            },
            () => {
                if (name !== 'label') {
                    this.updatelabel();
                } else {
                    this.checklabel();
                }
            }
        );
    };

    checklabel = () => {
        if (this.state.column.label.length >= 64) {
            this.setState({
                errors: { ...this.state.errors, label: translate('Label text is too long.') },
            });
        }
    };

    updatelabel = () => {
        this.setState((prevState) => {
            const { column, options, portAreas } = prevState;
            const option = options.find((x) => {
                return x.value === column.meta_data?.option;
            });
            const portArea = portAreas.find((x) => {
                return x.id === column.meta_data?.port_area_id;
            });
            return {
                column: {
                    ...column,
                    label: `${portArea?.name || ''} ${column.meta_data?.value || ''} ${
                        translate(column.meta_data?.parameter) || ''
                    } ${option?.label}`,
                },
            };
        });
    };

    render() {
        return (
            <Form onSubmit={this.handleFormSubmit} className="bg-white shadow p-4 w-full rounded">
                <div id={`Drag-${this.props.id}`} className="flex justify-between mb-2 no-select ">
                    <div className="text-lg leading-6 font-bold">{translate('Add custom column')} </div>
                    <div onTouchEnd={this.handleFormCancel} onClick={this.handleFormCancel} className="cursor-pointer">
                        <Icon>
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M18 6l-12 12" />
                            <path d="M6 6l12 12" />
                        </Icon>
                    </div>
                </div>
                {this.state.columns
                    .filter((x) => {
                        return x.status;
                    })
                    .reduce((sum, item) => sum + item.size, 0) > 22 && (
                    <Message className="mt-1" type="warning">
                        {translate('Some columns may not fit on the screen. Adding them may affect the grid display.')}
                    </Message>
                )}
                <FormGroup>
                    <Label>{translate('region')}</Label>
                    <DropList
                        capitalize
                        name="meta_data.port_area_id"
                        onChange={this.handleChange}
                        value={this.state?.column?.meta_data?.port_area_id}
                        options={this.state.portAreas.map((x) => {
                            return { label: translate(x.name), value: x.id };
                        })}
                    ></DropList>
                </FormGroup>
                <FormGroup>
                    <Label>{translate('Statistic')}</Label>
                    <DropList
                        capitalize
                        name="meta_data.value"
                        onChange={this.handleChange}
                        value={this.state?.column?.meta_data?.value}
                        options={[
                            { value: 'max', label: translate('max') },
                            { value: 'min', label: translate('min') },
                            { value: 'mean', label: translate('mean') },
                        ]}
                    ></DropList>
                </FormGroup>
                <FormGroup>
                    <Label>{translate('Parameter')}</Label>
                    <DropList
                        capitalize
                        name="meta_data.option"
                        onChange={this.handleChange}
                        value={this.state?.column?.meta_data?.option}
                        options={this.state.options}
                    ></DropList>
                </FormGroup>
                {['wind', 'wave', 'tide', 'watercurrent'].includes(this.state?.column?.meta_data?.option) ? (
                    <>
                        <FormGroup>
                            <Label>{translate('Instruments')}</Label>
                            <DropList
                                capitalize
                                name="meta_data.port_url_id"
                                onChange={this.handleChange}
                                value={this.state?.column?.meta_data?.port_url_id}
                                //@ts-ignore
                                options={this.props.port.port_urls
                                    .filter((x) => x.environmental_type === this.state?.column?.meta_data?.option)
                                    .map((x) => {
                                        return { value: x.id, label: x.description };
                                    })}
                            ></DropList>
                        </FormGroup>
                        <FormGroup>
                            <Label>{translate('Parameters')}</Label>
                            <DropList
                                capitalize
                                name="meta_data.parameter"
                                onChange={this.handleChange}
                                value={this.state?.column?.meta_data?.parameter}
                                //@ts-ignore
                                options={this.state.enviroDisplayOrder
                                    ?.filter((x) => x.key === this.state?.column?.meta_data?.option)
                                    .map((x) => x)[0]
                                    ?.list.map((x) => {
                                        return { value: x, label: translate(x) };
                                    })}
                            ></DropList>
                        </FormGroup>
                    </>
                ) : null}

                {/* */}

                <FormGroup comments={renderError(this.state.errors?.['label'])}>
                    <Label>{translate('label')}</Label>
                    <Input name="label" onChange={this.handleChange} value={this.state?.column?.label}></Input>
                </FormGroup>

                <div className="flex justify-end">
                    <div className="mt-5 flex gap-3">
                        <Button className="px-4 py-2 font-bold border border-gray-400" onClick={this.handleFormCancel}>
                            {translate('Cancel')}
                        </Button>
                        <Button type="submit" className="px-4 py-2 bg-primary-submit rounded-md text-white">
                            {translate('Save')}
                        </Button>
                    </div>
                </div>
            </Form>
        );
    }
}

const addGridColumnModelStateToProps = (state: RootState) => ({
    language: state.user.user.language,
    gridSettings: state.layout.gridSettings,
    port: state.port.port,
});

const addGridColumnModelDispatchToProps = (dispatch: Dispatch) => ({
    setGridSettingsAction: bindActionCreators(setGridSettingsAction, dispatch),
});

export default connect(addGridColumnModelStateToProps, addGridColumnModelDispatchToProps)(AddGridColumnModel);
